/**
 * Shop-bezogene Socket Handler
 *
 * Factory-Funktionen für Shop-Events.
 */

import { useCallback } from 'react';
import soundService from '../../soundService';

/** Spielername aus players-Array oder Fallback */
const resolvePlayerName = (players, index) =>
  players?.[index]?.name || `Spieler ${index + 1}`;

/**
 * Erstellt Shop-bezogene Event-Handler
 */
export function useShopHandlers(config) {
  const {
    dispatch,
    setShopItems,
    setShopJokers,
    setShopJokersPerPlayer,
    setShopCurrentPlayer,
    setShowShop,
    setShowRoundEnd,
    notify,
    myPlayerIndex,
    players,
  } = config;

  /**
   * Handler für shopPhaseStarted Event
   */
  const handleShopPhaseStarted = useCallback((data) => {
    soundService.play('shop_open');
    if (data.shopItems) setShopItems?.(data.shopItems);
    if (data.jokers) setShopJokers?.(data.jokers);
    if (data.shopJokersPerPlayer) setShopJokersPerPlayer?.(data.shopJokersPerPlayer);
    setShopCurrentPlayer?.(0);
    setShowShop(true);
    setShowRoundEnd(false);

    // Dispatch shop data to GameContext
    if (dispatch) {
      dispatch({
        type: 'GAME_STATE_UPDATE',
        payload: {
          shopBundlePerPlayer: data.shopBundlePerPlayer,
          shopJokerBundlePerPlayer: data.shopJokerBundlePerPlayer,
          shopJokersPerPlayer: data.shopJokersPerPlayer,
          shopItems: data.shopItems,
        },
      });
    }
  }, [dispatch, setShopItems, setShopJokers, setShopJokersPerPlayer, setShopCurrentPlayer, setShowShop, setShowRoundEnd]);

  /**
   * Handler für shopPlayerChanged Event
   */
  const handleShopPlayerChanged = useCallback((data) => {
    setShopCurrentPlayer?.(data.playerIndex);
  }, [setShopCurrentPlayer]);

  /**
   * Handler für sealPurchased Event
   */
  const handleSealPurchased = useCallback((data) => {
    soundService.play('shop_buy_seal');
  }, []);

  /**
   * Handler für jokerPurchased Event
   */
  const handleJokerPurchased = useCallback((data) => {
    soundService.play('shop_buy_joker');
    if (data.playerIndex !== myPlayerIndex && notify && data.jokerName) {
      notify(`${resolvePlayerName(players, data.playerIndex)} kauft ${data.jokerName}`, { icon: '➕', duration: 3000, priority: 'low' });
    }
  }, [myPlayerIndex, notify, players]);

  /**
   * Handler für jokerSold Event
   */
  const handleJokerSold = useCallback((data) => {
    soundService.play('shop_sell');
    if (data.playerIndex !== myPlayerIndex && notify && data.jokerName) {
      notify(`${resolvePlayerName(players, data.playerIndex)} verkauft ${data.jokerName}`, { icon: '➖', duration: 3000, priority: 'low' });
    }
  }, [myPlayerIndex, notify, players]);

  /**
   * Handler für bundlePurchased Event (V3.0)
   */
  const handleBundlePurchased = useCallback((data) => {
    soundService.play('booster_open');
  }, []);

  /**
   * Handler für jokerBundlePurchased Event
   */
  const handleJokerBundlePurchased = useCallback((data) => {
    soundService.play('booster_open');
    if (data.playerIndex !== myPlayerIndex && notify && data.jokersAdded?.length > 0) {
      const jokerNames = data.jokersAdded.map(j => j.name).join(', ');
      notify(`${resolvePlayerName(players, data.playerIndex)} kauft ${jokerNames}`, { icon: '➕', duration: 3000, priority: 'low' });
    }
  }, [myPlayerIndex, notify, players]);

  return {
    handleShopPhaseStarted,
    handleShopPlayerChanged,
    handleSealPurchased,
    handleJokerPurchased,
    handleJokerSold,
    handleBundlePurchased,
    handleJokerBundlePurchased,
  };
}

export default useShopHandlers;
